import React, { useState, useEffect } from "react";
import styled from 'styled-components';

import { fetchUserPlaylistsAPI, fetchAllPlaylistsAPI } from "../../controller/userController.js";

import PlaylistCard from "../scenes/playlist-card.js";
import NewPlaylist from "../scenes/playlist-new-popup.js";

const Container = styled.div`
    width: 100%;
    max-width: 1400px;
    margin: 0 auto;
    padding: 32px 20px;
`;

const HeaderSection = styled.div`
    margin-bottom: 32px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 16px;
`;

const Title = styled.h1`
    font-size: 36px;
    font-weight: 700;
    color: #1a1a1a;
    margin: 0;
    background: linear-gradient(135deg, #0f766e 0%, #06b6d4 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
`;

const PlaylistsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 24px;
    width: 100%;
    margin-top: 24px;
`;

const EmptyState = styled.div`
    grid-column: 1 / -1;
    text-align: center;
    padding: 60px 20px;
    color: #999;

    h3 {
        font-size: 20px;
        margin-bottom: 12px;
    }

    p {
        font-size: 16px;
        margin: 0;
    }
`;

const ToggleContainer = styled.div`
    display: flex;
    gap: 12px;
`;

const ToggleButton = styled.button`
    padding: 8px 16px;
    border-radius: 8px;
    border: 2px solid ${props => props.active ? '#0f766e' : '#e0e0e0'};
    background: ${props => props.active ? 'linear-gradient(135deg, #0f766e 0%, #06b6d4 100%)' : 'white'};
    color: ${props => props.active ? 'white' : '#666'};
    font-weight: 600;
    font-size: 14px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        border-color: #0f766e;
    }
`;

const OwnerBadge = styled.span`
    display: inline-block;
    background: #e0f2fe;
    color: #0369a1;
    font-size: 11px;
    font-weight: 600;
    padding: 2px 6px;
    border-radius: 4px;
    margin-left: 8px;
`;

export default function Playlists(props) {
    const [playlists, setPlaylists] = useState([]);
    const [allPlaylists, setAllPlaylists] = useState([]);
    const [viewAll, setViewAll] = useState(false);

    const fetchUserPlaylistsCallback = (data) => {
        setPlaylists(data);
    };

    const fetchAllPlaylistsCallback = (data) => {
        setAllPlaylists(data);
    };

    const refreshPlaylist = () => {
        fetchUserPlaylistsAPI(fetchUserPlaylistsCallback);
        if (props.isAdmin) {
            fetchAllPlaylistsAPI(fetchAllPlaylistsCallback);
        }
    };

    useEffect(() => {
        refreshPlaylist();
    }, []);

    const displayPlaylists = viewAll ? allPlaylists : playlists;

    return (
        <Container>
            <HeaderSection>
                <Title>{viewAll ? `All Playlists (${allPlaylists.length})` : `My Playlists (${playlists.length})`}</Title>
                {props.isAdmin && (
                    <ToggleContainer>
                        <ToggleButton active={!viewAll} onClick={() => setViewAll(false)}>My Playlists</ToggleButton>
                        <ToggleButton active={viewAll} onClick={() => setViewAll(true)}>All Playlists</ToggleButton>
                    </ToggleContainer>
                )}
            </HeaderSection>

            {Array.isArray(displayPlaylists) && displayPlaylists.length > 0 ? (
                <PlaylistsGrid>
                    {displayPlaylists.map((playlist, index) => (
                        <div key={playlist.id || index}>
                            <PlaylistCard
                                playlist={playlist}
                                roles={props.perms}
                                settings={{
                                    "menu": {
                                        addToFavorite: false,
                                        removeFromPlaylist: false,
                                        editPlaylist: !viewAll,
                                        deletePlaylist: !viewAll
                                    },
                                    playlistInfo: playlist
                                }}
                                refresh={refreshPlaylist}
                            />
                            {viewAll && playlist.username && <OwnerBadge>{playlist.username}</OwnerBadge>}
                        </div>
                    ))}
                </PlaylistsGrid>
            ) : (
                <EmptyState>
                    <h3>No Playlists Yet</h3>
                    <p>{viewAll ? 'No playlists found.' : 'Create your first playlist to get started!'}</p>
                </EmptyState>
            )}

            {!viewAll && <NewPlaylist refresh={refreshPlaylist} buttonType={'fixedButton'} />}
        </Container>
    );
}
